import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Descriptions, Button, Tooltip, Modal, Icon } from 'antd';
import { formatDate } from '../../../../shared/packages/dateTime';
import Btn, { btnTypes } from '../../../../../../components/btn';
import Card from '../Card';
import { useModalMethods } from '../../../../shared/packages/modalManager';
import { modalNames } from '../../../../shared/configs/modal';
import { roomAttachmentModalActions } from '../../../../shared/configs/attachments';
import { useCancelUnilateral } from '../../../../api/cancelUnilateral';
import { roomAcceptanceStatusIdsMap } from '../../../../../../constants/room/lifeCycle';
import { useAcceptanceViewFlatsInvalidate } from '../../../../api/acceptanceViewFlats';
import { useInvalidateRoomInfo } from '../../../../api/roomInfo';
import { useInvalidateRoomAttachments } from '../../../../api/roomAttachments';
import { useUserAcceptanceRole } from '../../../../shared/hooks/userAcceptanceRole';
import { actionCreators, selectors, sliceKeys } from '../../../../model';
import { ReactComponent as WebIcon } from '../../../../../../resource/WebIconAdaptive.svg';
import styles from './index.module.scss';

const { selectedRecordChanged } = actionCreators[sliceKeys.attachmentsFilter];
const { createIsSelectedRecordSelector } =
  selectors[sliceKeys.attachmentsFilter];

const descriptionItems = [
  {
    label: 'Дата',
    key: 'date',
    getValue: ({ record }) =>
      record.acceptanceDate ? formatDate(record.acceptanceDate) : '-',
  },
  {
    label: 'Время',
    key: 'time',
    getValue: ({ record }) => record.acceptanceTime ?? '-',
  },
  {
    label: 'Записан(а)',
    key: 'name',
    getValue: ({ record }) => record.client?.name ?? '-',
  },
  {
    label: 'Прием',
    key: 'type',
    getValue: ({ record }) =>
      `${record.withExpert ? 'с экспертом' : 'без эксперта'} ${
        record.byProxy ? 'по доверенности' : ''
      }`,
  },
  {
    label: 'Результат',
    key: 'status',
    getValue: Result,
  },
];

const RecordBlock = ({ record, room, hasVisibleDocsBtn }) => {
  const role = useUserAcceptanceRole();

  return (
    <Card dark data={true}>
      <Descriptions className={styles.descriptions} layout="vertical">
        {descriptionItems.map(({ key, label, getValue }) => (
          <Descriptions.Item key={key} label={label}>
            {getValue({ record, room })}
          </Descriptions.Item>
        ))}
      </Descriptions>

      {Boolean(record.worker) && <WorkerBlock name={record.worker.name} />}

      {role.isDirectorOrManager && (
        <div className={styles.btnsGroup}>
          <AddDocuments room={room} record={record} />
          {hasVisibleDocsBtn && <ViewDocumentsForRecord record={record} />}
        </div>
      )}
    </Card>
  );
};

export default RecordBlock;

function AddDocuments({ room, record }) {
  const { open } = useModalMethods();

  const handleClick = () => {
    open(modalNames.attachmentsForm, {
      actionType: roomAttachmentModalActions.addAttachment,
      room,
      record,
    });
  };

  return (
    <Btn type={btnTypes.secondary} block onClick={handleClick}>
      Добавить документы
    </Btn>
  );
}

function ViewDocumentsForRecord({ record }) {
  const recordId = record?.id;

  const dispatch = useDispatch();

  const selectIsSelectedRecord = useMemo(
    () => createIsSelectedRecordSelector(recordId),
    [recordId]
  );
  const isSelectedRecord = useSelector(selectIsSelectedRecord);

  const handleClick = () => {
    dispatch(selectedRecordChanged(recordId));
  };

  return (
    <Tooltip
      title={
        isSelectedRecord
          ? 'Показать все документы'
          : 'Показать документы по данной записи'
      }
    >
      <Btn
        type={btnTypes.secondary}
        block
        onClick={handleClick}
        style={{
          border: '1px solid transparent',
          borderColor: isSelectedRecord ? '#0995ed' : 'transparent',
        }}
      >
        Документы
      </Btn>
    </Tooltip>
  );
}

function Result({ record, room }) {
  if (
    record.acceptanceStatusAddressLifeCycle?.id !==
    roomAcceptanceStatusIdsMap.oneWayTransfer
  ) {
    return <>{record.acceptanceStatusAddressLifeCycle?.name ?? '-'}</>;
  }

  return <UnilateralResult record={record} room={room} />;
}

function UnilateralResult({ record, room }) {
  const role = useUserAcceptanceRole();

  return (
    <div className={styles.unilateralResult}>
      <span>{record.acceptanceStatusAddressLifeCycle.name}</span>
      {role.isDirectorOrManager && (
        <CancelUnilateralBtn roomId={room?.id} recordId={record.id} />
      )}
    </div>
  );
}

function CancelUnilateralBtn({ roomId, recordId }) {
  const invalidateFlats = useAcceptanceViewFlatsInvalidate();
  const invalidateRoomInfo = useInvalidateRoomInfo();
  const invalidateAttachments = useInvalidateRoomAttachments();

  const { mutateAsync } = useCancelUnilateral({
    onSuccess: () => {
      invalidateFlats();
      invalidateRoomInfo({ addressId: roomId });
      invalidateAttachments({ addressId: roomId });
    },
  });

  const onRemove = () => {
    Modal.confirm({
      title: 'Отменить одностороннюю передачу?',
      centered: true,
      onOk: async () => {
        await mutateAsync({
          addressId: roomId,
          acceptanceClientId: recordId,
        });
      },
      onCancel: () => {},
    });
  };

  return (
    <Tooltip title="Отменить одностороннюю передачу">
      <Button icon="delete" className={styles.cancelBtn} onClick={onRemove} />
    </Tooltip>
  );
}

function WorkerBlock({ name }) {
  return (
    <div className={styles.workerWrapper}>
      <span className={styles.workerLabel}>Записал(а):</span>
      <span className={styles.workerContent}>
        <Icon component={WebIcon} className={styles.workerIcon} />
        {name ?? '-'}
      </span>
    </div>
  );
}
